import { LocalDateTime } from '@js-joda/core'
import { RealTimeSttTranscriptElementBase } from '~app/features/real-time-playback/smart/real-time-stt/shared/RealTimeSttTranscriptElementBase'
import { RealTimeSttRemark } from '../RealTimeSttRemark'
import { RealTimeSttRemarkUtils } from './RealTimeSttRemarkUtils'

export type RealTimeRemarkSearchHighlightingType = 'SelectedResult' | 'HoveredResult'
export interface RealTimeRemarkSearchHighlightingContext {
  highlightType: RealTimeRemarkSearchHighlightingType

  readonly contentHighlightHtml?: string
  readonly speakerHighlightHtml?: string
}

export interface RealTimeSttRemarkWithMetadata
  extends Omit<RealTimeSttRemark, 'isSealed'>,
    RealTimeSttTranscriptElementBase {
  readonly type: 'Remark'
  readonly isFirstRemarkOfSpeaker: boolean
  readonly firstRemarkIdOfSpeaker: string
  readonly isFirstRemarkOfMinute: boolean

  /**
   * Contains metadata about how to highlight parts of the content that match a search result.
   */
  readonly searchHighlightingContext?: RealTimeRemarkSearchHighlightingContext
}

export interface RealTimeSttRemarkMatchInfo {
  remarkId: string
  startTime: LocalDateTime
}
export interface RealTimeSearchHighlightRemarkMatchInfo {
  highlightContext: RealTimeRemarkSearchHighlightingContext
  remarkMatchInfo: RealTimeSttRemarkMatchInfo
}

export interface RealTimeSearchHighlights {
  selected: RealTimeSearchHighlightRemarkMatchInfo | undefined
  hovered: RealTimeSearchHighlightRemarkMatchInfo | undefined
}

export class RealTimeSttRemarkWithMetadataBuilder {
  /**
   * Regeneration requires the remarks to be in order
   */
  static regenerateMetadata(
    remarks: readonly RealTimeSttRemarkWithMetadata[],
    searchHighlightMatches: RealTimeSearchHighlights,
  ): RealTimeSttRemarkWithMetadata[] {
    return remarks.map((remark, idx) => {
      const firstRemarkIdOfSpeaker = RealTimeSttRemarkUtils.getFirstRemarkOfSpeaker(remarks, idx).id

      const searchHighlightingContext = RealTimeSttRemarkUtils.getSearchHighlightingContextForRemark(
        remark,
        searchHighlightMatches,
      )
      return {
        ...remark,
        isFirstRemarkOfMinute: RealTimeSttRemarkUtils.isFirstRemarkOfMinute(remarks[idx], remarks[idx - 1]),
        isFirstRemarkOfSpeaker: firstRemarkIdOfSpeaker === remark.id,
        firstRemarkIdOfSpeaker,
        searchHighlightingContext,
      }
    })
  }

  static buildFromRemarks(
    segmentId: string,
    remarks: RealTimeSttRemark[],
    searchHighlights: RealTimeSearchHighlights,
  ): RealTimeSttRemarkWithMetadata[] {
    const mappedRemarks: RealTimeSttRemarkWithMetadata[] = remarks.map(r => ({
      type: 'Remark' as const,
      endTime: r.endTime,
      speakerId: r.speakerId,
      speakerName: r.speakerName,
      startTime: r.startTime,
      id: r.id,
      segmentId,
      content: r.content,
      isSealed: !!r.isSealed,
      isSealedContentMarker: r.isSealedContentMarker,
      grouping: r.grouping,
      liveResult: r.liveResult,
      // dummy values - will be overwritten in  regenerateMetadata
      isFirstRemarkOfMinute: false,
      isFirstRemarkOfSpeaker: false,
      firstRemarkIdOfSpeaker: '',
    }))
    return this.regenerateMetadata(mappedRemarks, searchHighlights)
  }
}
